import React, { useState } from "react"
import { BarChart2, ChevronDown, Clock, Loader2, X } from "lucide-react"
import { TiptapReadOnlyContent } from "../../../../component/tiptapEditor"
import { paperDisplayTitle, type PaperSession } from "../types"
import {
  formatPaperDate,
  formatPaperElapsed,
} from "../utils/formatPaperTime"
import { awaitingMarks, scoreOf } from "../utils/paperStatus"
import { scoreBand } from "../utils/scoreThreshold"
import { StatusBadge } from "./SessionCard"
import PaperQuestionStem from "./PaperQuestionStem"
import {
  answerPlainSnippet,
  hasAnswerContent,
} from "../utils/hasAnswerContent"
import "./AnalyticsView.style.scss"

interface AnalyticsViewProps {
  session: PaperSession
  onClose: () => void
}

const AnalyticsView = ({ session, onClose }: AnalyticsViewProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const { score, max, pct: percentage, isPending } = scoreOf(session)
  const pendingMarks = awaitingMarks(session)
  const band = scoreBand(percentage)
  const totalTime = session.totalTimeSpentSeconds
  const answered = session.questions.filter((q) => q.status !== "unanswered")
    .length
  const dash = Math.min(percentage, 100) * 2.89

  return (
    <div className="analytics-view">
      <header className="analytics-view__header">
        <div className="analytics-view__title">
          <button type="button" onClick={onClose} aria-label="Close analytics">
            <X size={20} />
          </button>
          <div>
            <h2>{paperDisplayTitle(session)} — Analytics</h2>
            <p>{formatPaperDate(session.lastActiveAt)}</p>
          </div>
        </div>
        <StatusBadge status={session.status} />
      </header>

      <main className="analytics-view__main">
        <div className="analytics-view__inner">
          <div className="analytics-view__metrics">
            <div className="analytics-view__metric">
              <div className="analytics-view__ring">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="46" className="is-track" />
                  <circle
                    cx="50"
                    cy="50"
                    r="46"
                    className={`is-fill is-${isPending ? "mid" : band}`}
                    strokeDasharray={`${isPending ? 0 : dash} 289`}
                  />
                </svg>
                <span>{isPending ? "—" : `${percentage}%`}</span>
              </div>
              <h3>Overall Score</h3>
              <p>
                {isPending
                  ? "Awaiting marking"
                  : `${score} / ${max} marks`}
              </p>
            </div>

            <div className="analytics-view__metric">
              <div className="analytics-view__icon is-time">
                <Clock size={32} />
              </div>
              <h3>Time Taken</h3>
              <p>{formatPaperElapsed(totalTime)}</p>
              {session.questions.length > 0 && (
                <p className="is-sub">
                  ~{formatPaperElapsed(
                    Math.round(totalTime / session.questions.length)
                  )}{" "}
                  per question
                </p>
              )}
            </div>

            <div className="analytics-view__metric">
              <div className="analytics-view__icon is-completion">
                <BarChart2 size={32} />
              </div>
              <h3>Completion</h3>
              <p>
                {answered} / {session.questions.length} answered
              </p>
              <p className="is-sub">
                {session.questions.length
                  ? Math.round((answered / session.questions.length) * 100)
                  : 0}
                % attempted
              </p>
            </div>
          </div>

          {pendingMarks ? (
            <div className="analytics-view__marking">
              <Loader2 size={28} className="is-spinning" />
              <h3>Marking in progress</h3>
              <p>Your questions will show here when the marks are ready.</p>
            </div>
          ) : (
          <div className="analytics-view__breakdown">
            <h3>Question Breakdown</h3>
            <div className="analytics-view__rows">
              {session.questions.map((q, idx) => {
                const qPct =
                  q.maxScore > 0 && q.score !== null
                    ? Math.round((q.score / q.maxScore) * 100)
                    : null
                const qBand = scoreBand(qPct ?? 0)
                const open = openIndex === idx
                return (
                  <div
                    key={`${q.questionId}-${idx}`}
                    className={`analytics-view__row${open ? " is-open" : ""}`}
                  >
                    <div className="analytics-view__row-main">
                      <button
                        type="button"
                        className="analytics-view__row-head"
                        aria-expanded={open}
                        onClick={() => setOpenIndex(open ? null : idx)}
                      >
                        <span className="analytics-view__qnum">Q{idx + 1}</span>
                        <span className="analytics-view__chip">{q.topic}</span>
                        {q.status === "unanswered" && (
                          <span className="analytics-view__chip is-skip">
                            Skipped
                          </span>
                        )}
                        <ChevronDown size={16} className="analytics-view__chevron" />
                      </button>
                      {!open && (
                        <div
                          className="analytics-view__preview"
                          onClick={() => setOpenIndex(idx)}
                        >
                          <div className="analytics-view__qtext">
                            <PaperQuestionStem
                              html={q.questionText}
                              artifacts={q.questionArtifacts}
                              compact
                            />
                          </div>
                          {hasAnswerContent(q.answerText) && (
                            <p className="analytics-view__answer">
                              &ldquo;
                              {answerPlainSnippet(q.answerText) ||
                                "Answer provided"}
                              &rdquo;
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="analytics-view__row-score">
                      {q.score !== null ? (
                        <div className={`analytics-view__score is-${qBand}`}>
                          {q.score} / {q.maxScore}
                        </div>
                      ) : q.status === "unanswered" ? (
                        <span className="analytics-view__na">Not attempted</span>
                      ) : (
                        <span className="analytics-view__na">
                          {isPending ? "Pending" : "—"}
                        </span>
                      )}
                    </div>
                    {q.score !== null && q.maxScore > 0 && (
                      <div className="analytics-view__bar">
                        <div
                          className={`is-${qBand}`}
                          style={{ width: `${qPct}%` }}
                        />
                      </div>
                    )}
                    {open && (
                      <div className="analytics-view__detail">
                        <PaperQuestionStem
                          html={q.questionText}
                          artifacts={q.questionArtifacts}
                        />
                        <div className="analytics-view__detail-block">
                          <h5>Your answer</h5>
                          {hasAnswerContent(q.answerText) ? (
                            <TiptapReadOnlyContent html={q.answerText} />
                          ) : (
                            <p className="is-empty">No answer provided</p>
                          )}
                        </div>
                        {!!q.criteriaBreakdown?.length && (
                          <div className="analytics-view__detail-block">
                            <h5>Mark breakdown</h5>
                            {q.criteriaBreakdown.map((criterion) => (
                              <div
                                key={criterion.label}
                                className="analytics-view__criterion"
                              >
                                <div>
                                  <span>{criterion.label}</span>
                                  <em>
                                    {criterion.marksAwarded}/
                                    {criterion.marksAvailable}
                                  </em>
                                </div>
                                {criterion.rationale ? (
                                  <p>{criterion.rationale}</p>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                        {(!!q.strengths?.length || !!q.improvements?.length) && (
                          <div className="analytics-view__notes">
                            {!!q.strengths?.length && (
                              <div>
                                <h5>Strengths</h5>
                                <ul>
                                  {q.strengths.map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {!!q.improvements?.length && (
                              <div>
                                <h5>Areas to improve</h5>
                                <ul>
                                  {q.improvements.map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        {q.suggestedAnswer ? (
                          <div className="analytics-view__detail-block">
                            <h5>Suggested answer</h5>
                            <p>{q.suggestedAnswer}</p>
                          </div>
                        ) : null}
                        {q.examinerFeedback ? (
                          <div className="analytics-view__detail-block">
                            <h5>Examiner note</h5>
                            <p>&ldquo;{q.examinerFeedback}&rdquo;</p>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default AnalyticsView
